import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Video, Image as ImageIcon, UploadCloud, Loader2 } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";

const CATEGORIES = ["cricket_ground", "indoor_box", "academy_drills", "badminton", "gym"];

const EMPTY = { title: "", category: "cricket_ground", image_url: "", caption: "", media_type: "image", video_url: "" };

function VideoUploader({ value, onUploaded, onCleared }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (files) => {
    if (!files || !files[0]) return;
    const file = files[0];
    if (!file.type.startsWith("video/")) { toast.error("Only video files"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Video must be under 25 MB"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/video", form, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  if (value) {
    return (
      <div className="relative border border-white/20 bg-[#0B1120] group" data-testid="video-preview">
        <video src={value} className="w-full h-40 object-cover" muted playsInline autoPlay loop />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
          <button type="button" onClick={() => inputRef.current?.click()} className="text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 bg-black/70 hover:bg-brand-blue hover:text-white">Replace</button>
          <button type="button" onClick={onCleared} className="text-xs font-mono uppercase tracking-widest border border-red-500/60 text-red-400 px-3 py-2 bg-black/70">Remove</button>
        </div>
        <input ref={inputRef} type="file" accept="video/*" hidden onChange={(e) => handle(e.target.files)} />
      </div>
    );
  }
  return (
    <div onClick={() => inputRef.current?.click()} data-testid="video-uploader"
      className="border-2 border-dashed border-white/20 hover:border-white/40 bg-[#0B1120] cursor-pointer py-10 flex flex-col items-center gap-2">
      {busy ? <><Loader2 className="brand-blue animate-spin" size={22} /><div className="text-xs font-mono brand-blue uppercase tracking-widest">Uploading…</div></>
        : <><UploadCloud size={24} className="text-white/50" /><div className="text-xs font-mono text-white/60 uppercase tracking-widest">Upload video</div><div className="text-[10px] text-white/40">MP4/WEBM · max 25MB</div></>}
      <input ref={inputRef} type="file" accept="video/*" hidden onChange={(e) => handle(e.target.files)} />
    </div>
  );
}

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (form.media_type === "video" && !form.video_url) { toast.error("Upload the video first"); return; }
    if (!form.image_url) { toast.error(form.media_type === "video" ? "Poster image is required" : "Upload an image"); return; }
    try {
      await api.post("/admin/gallery", form);
      toast.success(form.media_type === "video" ? "Video added" : "Image added");
      setForm(EMPTY);
      setShowAdd(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/admin/gallery/${id}`);
    load();
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Media Library</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Gallery.</h1>
          <p className="text-sm text-white/60 mt-2">Images shown in the homepage slideshow. Videos autoplay silently in the ambient panel.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="new-gallery-btn" className="bg-brand-blue text-white px-5 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2">
          <Plus size={12} /> Add media
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`admin-gallery-${it.id}`} className="border border-white/12 bg-[#131A2B] group relative">
            {it.media_type === "video" && it.video_url ? (
              <div className="relative">
                <video src={it.video_url} className="w-full aspect-square object-cover" muted playsInline preload="metadata" />
                <span className="absolute top-2 left-2 bg-brand-orange text-white text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 flex items-center gap-1"><Video size={10} /> Video</span>
              </div>
            ) : (
              <img src={it.image_url} alt={it.title} className="w-full aspect-square object-cover" />
            )}
            <div className="p-3">
              <div className="font-bold text-sm truncate">{it.title}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{it.category}</div>
            </div>
            <button onClick={() => del(it.id)} data-testid={`gallery-delete-${it.id}`} className="absolute top-2 right-2 p-1.5 bg-black/70 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl uppercase font-black">Add media</h3>
              <button onClick={() => setShowAdd(false)}><X size={18} /></button>
            </div>

            <div className="flex gap-2 mb-5" data-testid="media-type-toggle">
              {[
                { key: "image", label: "Image", icon: ImageIcon },
                { key: "video", label: "Video", icon: Video },
              ].map((o) => (
                <button
                  key={o.key} type="button"
                  onClick={() => setForm({ ...form, media_type: o.key })}
                  data-testid={`media-type-${o.key}`}
                  className={`flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
                    form.media_type === o.key ? "border-brand-blue bg-brand-blue/10 brand-blue" : "border-white/20 text-white/50"
                  }`}
                >
                  <o.icon size={12} /> {o.label}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="gallery-title" className="w-full bg-transparent border border-white/20 py-3 px-4" />

              {form.media_type === "image" ? (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Image</div>
                  <ImageUploader
                    value={form.image_url}
                    onUploaded={(url) => setForm({ ...form, image_url: url })}
                    onCleared={() => setForm({ ...form, image_url: "" })}
                    label="Upload gallery image"
                    testid="gallery-uploader"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Video file</div>
                    <VideoUploader
                      value={form.video_url}
                      onUploaded={(url) => setForm({ ...form, video_url: url })}
                      onCleared={() => setForm({ ...form, video_url: "" })}
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Poster / thumbnail</div>
                    <ImageUploader
                      value={form.image_url}
                      onUploaded={(url) => setForm({ ...form, image_url: url })}
                      onCleared={() => setForm({ ...form, image_url: "" })}
                      label="Upload poster image"
                      testid="gallery-poster"
                    />
                  </div>
                </>
              )}

              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="gallery-category" className="w-full bg-[#131A2B] border border-white/20 py-3 px-4">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
              </select>
              <input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="Caption" className="w-full bg-transparent border border-white/20 py-3 px-4" />
              <button onClick={save} data-testid="gallery-save" className="w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
